import { Routes } from '@angular/router';
import { needs } from '@core/config/workspace-route.util';
import { personaMatch, typeMatch } from '@core/guards/workspace-match.guard';

/**
 * Education workspace routes (staff persona).
 *
 * Overlapping flat URLs (`assessments`, `campaigns`, `evaluation`, `exports`,
 * home) use `canMatch: typeMatch('education')` / `personaMatch('educationStaff')`
 * and MUST be registered before the business defaults in `WORKSPACE_PAGES`.
 *
 * Education-only surfaces: students, classes.
 * Campaigns URL maps to the assignments feature folder.
 */
export const EDUCATION_WORKSPACE_ROUTES: Routes = [
  // ── Home ───────────────────────────────────────────────────────────────────
  {
    path: '',
    pathMatch: 'full',
    canMatch: [personaMatch('educationStaff')],
    loadComponent: () =>
      import('../../pages/workspaces/education/home/home.page').then(
        (m) => m.EducationHomePage,
      ),
  },

  // ── Assessments ────────────────────────────────────────────────────────────
  {
    path: 'assessments',
    canMatch: [typeMatch('education')],
    ...needs('assessment.edit', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'assessments/studio',
    canMatch: [typeMatch('education')],
    ...needs('assessment.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'assessments/templates',
    canMatch: [typeMatch('education')],
    ...needs('assessment.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/templates/assessment-templates.page'
      ).then((m) => m.AssessmentTemplatesPage),
  },
  {
    path: 'assessments/detail',
    canMatch: [typeMatch('education')],
    ...needs('assessment.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'assessments/versions',
    canMatch: [typeMatch('education')],
    ...needs('assessment.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'assessments/taking',
    canMatch: [typeMatch('education')],
    ...needs('assessment.view'),
    loadComponent: () =>
      import(
        '../../pages/assessments/campaigns/assigned-assessments.page'
      ).then((m) => m.AssignedAssessmentsPage),
  },

  // ── Assignments (URL: /campaigns) ──────────────────────────────────────────
  {
    path: 'campaigns',
    canMatch: [typeMatch('education')],
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/campaigns/assessment-campaigns.page'
      ).then((m) => m.AssessmentCampaignsPage),
  },
  {
    path: 'campaigns/launch',
    canMatch: [typeMatch('education')],
    ...needs('campaign.create', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },
  {
    path: 'campaigns/monitor',
    canMatch: [typeMatch('education')],
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },
  {
    path: 'campaigns/participants',
    canMatch: [typeMatch('education')],
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },
  {
    path: 'campaigns/results',
    canMatch: [typeMatch('education')],
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },
  {
    path: 'campaigns/results/individual',
    canMatch: [typeMatch('education')],
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'campaigns/audit-trail',
    canMatch: [typeMatch('education')],
    loadComponent: () =>
      import(
        '../../pages/workspaces/education/assignments/audit-trail/audit-trail.page'
      ).then((m) => m.EducationAuditTrailPage),
  },
  {
    path: 'campaigns/evidence-export',
    canMatch: [typeMatch('education')],
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'campaigns/reminders',
    canMatch: [typeMatch('education')],
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },

  // ── Evaluation ─────────────────────────────────────────────────────────────
  {
    path: 'evaluation',
    canMatch: [typeMatch('education')],
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'evaluation/review',
    canMatch: [typeMatch('education')],
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'evaluation/history',
    canMatch: [typeMatch('education')],
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'evaluation/ai-grading',
    canMatch: [typeMatch('education')],
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },

  // ── Students (education-canonical people directory) ────────────────────────
  {
    path: 'students',
    ...needs('participants.view'),
    loadComponent: () =>
      import('../../pages/workspaces/education/students/students.page').then(
        (m) => m.EducationStudentsPage,
      ),
  },
  {
    path: 'students/profile',
    ...needs('participants.view'),
    loadComponent: () =>
      import(
        '../../pages/workspaces/shared/participant-profile/participant-profile.page'
      ).then((m) => m.ParticipantProfilePage),
  },
  {
    path: 'students/invitations',
    ...needs('participants.manage'),
    loadComponent: () =>
      import('../../pages/workspaces/shared/invitations/invitations.page').then(
        (m) => m.InvitationsPage,
      ),
  },
  {
    path: 'students/import',
    ...needs('participants.manage'),
    loadComponent: () =>
      import(
        '../../pages/workspaces/shared/participants-import/participants-import.page'
      ).then((m) => m.ParticipantsImportPage),
  },
  {
    path: 'students/followup',
    ...needs('participants.manage', { minimalShell: true }),
    loadComponent: () =>
      import(
        '../../pages/workspaces/shared/participant-followup/participant-followup.page'
      ).then((m) => m.ParticipantFollowupPage),
  },
  // Legacy / typed /participants → education students page.
  // (redirectTo + canMatch can't be combined, so load directly.)
  {
    path: 'participants',
    canMatch: [typeMatch('education')],
    ...needs('participants.view'),
    loadComponent: () =>
      import('../../pages/workspaces/education/students/students.page').then(
        (m) => m.EducationStudentsPage,
      ),
  },

  // ── Classes ────────────────────────────────────────────────────────────────
  // Business workspaces render the same backend Group entity as Teams under
  // `/groups`, so these must not match outside education.
  {
    path: 'classes',
    canMatch: [typeMatch('education')],
    ...needs('classes.view'),
    loadComponent: () =>
      import('../../pages/workspaces/education/classes/classes.page').then(
        (m) => m.ClassesPage,
      ),
  },
  {
    path: 'classes/detail',
    canMatch: [typeMatch('education')],
    ...needs('classes.view'),
    loadComponent: () =>
      import(
        '../../pages/workspaces/education/classes/detail/class-detail.page'
      ).then((m) => m.ClassDetailPage),
  },
  // Legacy / typed /groups → education classes page.
  // (redirectTo + canMatch can't be combined, so load directly.)
  {
    path: 'groups',
    canMatch: [typeMatch('education')],
    ...needs('classes.view'),
    loadComponent: () =>
      import('../../pages/workspaces/education/classes/classes.page').then(
        (m) => m.ClassesPage,
      ),
  },

  // ── Exports ────────────────────────────────────────────────────────────────
  {
    path: 'exports',
    canMatch: [typeMatch('education')],
    ...needs('campaign.manage'),
    loadComponent: () =>
      import('../../pages/workspaces/shared/exports/exports.page').then(
        (m) => m.ExportsPage,
      ),
  },
];
